import React, { useState, useEffect, useCallback, useRef } from 'react';
import './gridStatus.css'
import GridPoint from '../../components/cards/gridPoint';
import { toast } from 'sonner';
import axios from 'axios';
import Cookies from 'js-cookie';
import { ClipLoader } from "react-spinners";
import { useTranslation } from 'react-i18next';
import { patchGridPoints, getStatus, getVessel, getGridPointsStatus, getCompany } from '../../services/requests';
import { Company, Grid, GridProps, Status, Vessels } from '../../interfaces/interfaces';

const GridStatus: React.FC = () => {
    const [statusOptions, setStatusOptions] = useState<string[]>([]);
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const [counter, setCounter] = useState(60);
    const [data, setData] = useState<GridProps[]>([
        {
            id: 0,
            idmonaguillo: "",
            name: "",
            active: false,
            orden: false,
            disponible: false,
            initialStatus: "",
            initialStatus2: 0,
            reserva: null,
            vessel: [],
        }
    ]);
    const [dataCompany, setDataCompany] = useState<Company[]>([]);
    const [dataVessel, setDataVessel] = useState<Vessels[]>([]);
    const [dataStatus, setDataStatus] = useState<Status[]>([]);
    const nempresa = Cookies.get('Nempresa');


    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());
    const dateTemp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    const hasShownToastRef = useRef(false);
    const [showModal, setShowModal] = useState(false);

    /**
     * Función para obtener y transformar los datos de la API (`/grid/` y `/status/`) 
     * según los parámetros actuales de la empresa (`nempresa`).
     * - Realiza dos solicitudes `GET` para obtener información del grid y sus estados.
     * - Combina y transforma los datos para actualizar el estado local `setData`.
     * - Maneja errores, incluyendo timeout por conexión.
     * Dependencias: [token, apiUrl, nempresa].
     */
    const getInformation = useCallback(async () => {
        try {

            try {
                const response = await getGridPointsStatus();

                if (!hasShownToastRef.current && response.status >= 200 && response.status < 300) {
                    toast.success(`${response.data.message}`);
                    hasShownToastRef.current = true;
                }

                const responseStatus = await getStatus();

                const responseVessel = await getVessel();

                const responseCompany = await getCompany();

                setDataCompany(responseCompany.data.data as Company[]);
                setDataStatus(responseStatus.data.data as Status[]);

                const filteredVessel = (responseVessel.data.data as Vessels[]).filter(vessel => nempresa === '' || vessel.Nempresa === Number(nempresa))
                setDataVessel(filteredVessel);

                const filteredStatusAll = (responseStatus.data.data as Status[]).filter(item => item.Tipo === "Mg");
                const uniqueStatuses = Array.from(new Set(filteredStatusAll.map(item => item.Testado))).sort();
                setStatusOptions(uniqueStatuses);

                const rawData = (response.data.data as Grid[]).map(grid => {
                    if (grid.Disponible === false) {
                        return {
                            ...grid,
                            Status: 5
                        };
                    }
                    return grid;
                });

                const transformedData = rawData
                    .map(grid => {
                        let statusMatch = filteredStatusAll.find(item => item.Nestado === grid.Status);

                        if (grid.Status === 5) {
                            statusMatch = {
                                Nestado: 5,
                                Testado: 'Desactivado',
                                Tipo: 'Mg'
                            };
                        }

                        return {
                            id: grid.Nmonaguillo,
                            idmonaguillo: grid.IDmonaguillo,
                            name: grid.Tmonaguillo || "",
                            active: grid.Activo,
                            orden: grid.Orden,
                            disponible: grid.Disponible,
                            initialStatus: statusMatch ? statusMatch.Testado.toString() : "",
                            initialStatus2: statusMatch ? statusMatch.Nestado : 0,
                            reserva: grid.Reserva ? grid.Reserva : null,
                            vessel: []
                        };
                    })
                    .sort((a, b) => a.name.localeCompare(b.name));

                setData(transformedData);
            } catch (error) {
                if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
                    console.log("Tiempo de espera agotado. Continuando con el proceso...");
                } else {
                    console.error("Error en la petición:", error);
                }
            }

        } catch (error) {
            toast.error(`Error: ${error}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, [nempresa]);

    /**
     * Efecto que establece un contador regresivo de 120 segundos.
     * Cuando el contador llega a 0, se recarga la página y el contador se reinicia.
     * El intervalo se limpia al desmontar el componente.
     */
    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date();
            const seconds = now.getSeconds();
            const remaining = 60 - seconds;

            setCounter(remaining);
            if (seconds === 0 && showModal === false) {
                setLoading(true);
                getInformation();
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [getInformation]);

    /**
     * Efecto que ejecuta `getInformation` una vez al cargar el componente
     * o cada vez que su dependencia (`getInformation`) cambie.
     */
    useEffect(() => {
        getInformation();
    }, [getInformation]);

    /**
     * Cambia el estado de un monaguillo en el grid.
     * @param name - Nombre del monaguillo a modificar.
     * @param newStatus - Nuevo estado que se desea establecer. Puede ser:
     *                    'Available', 'In use', 'Unsincronized' o 'No conection'.
     * - Valida que el monaguillo esté sincronizado y autorizado para cambiar de estado.
     * - Determina el nuevo estado lógico (`Disponible` y `Activo`).
     * - Realiza una petición PATCH a la API para actualizar el estado.
     * - Recarga la página después de la operación.
     */
    const handleStatusChange = async (name: string, newStatus: number, vessel: string) => {

        const statusMatch = data.find(item => item.name === name);

        if (statusMatch?.active === true && statusMatch?.orden === false) {
            toast.error(`${t('gridStatus.gridPointOutOfSyncPleaseWaitAFewMinutesOrContactAdministration')}`);
            return;
        }

        setLoading(true);

        try {
            let Available: boolean = false;
            let Active: boolean = false;

            if (newStatus === 4) {
                Available = false;
                Active = false;
            } else if (newStatus === 1 || (statusMatch?.active === false && statusMatch?.orden === true)) {
                Available = true;
                Active = false;
            } else if (newStatus === 2) {
                Available = true;
                Active = true;
            } else if (newStatus === 3) {
                if (statusMatch?.active === true) {
                    Available = true;
                    Active = false;
                } else if (statusMatch?.active === false) {
                    Available = true;
                    Active = true;
                }
            }

            const statusMatchData = dataStatus.filter(item => item.Nestado === newStatus);
            const filteredVessel = dataVessel.filter(item => item.Tembarcacion === vessel);

            const body = {
                id: statusMatch?.id,
                Disponible: Available,
                Activo: Active,
                Nconexion: statusMatch?.reserva?.Nconexion,
                Nreserva: statusMatch?.reserva?.Nreserva,
                Nembarcacion: filteredVessel[0].Nembarcacion,
            };

            const response = await patchGridPoints(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
            }

        } catch (error: any) {
            let message = `${t('reports.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.status === 422) {
                message = `${error.response.data?.message}`;
                setLoading(true);
                getInformation();
            } else if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
        }
    };

    /**
     * Filtra los puntos del grid según el estado seleccionado.
     * - Si `filterStatus` es `'all'`, se incluyen todos los puntos.
     * - Si se selecciona un estado específico, solo se incluyen los que coinciden con ese estado en `initialStatus`.
     * @returns Un array de puntos del grid filtrados por el estado.
     */
    const filteredGridPoints = data.filter(
        (item) => filterStatus === 'all' || item.initialStatus === filterStatus
    );

    const selectCompany = (reserva: any): string => {
        if (!reserva) return "Sin empresa";
        const vesselInfo = dataVessel.find(v => v.Nembarcacion === reserva.vessel.Nembarcacion);
        const companyInfo = dataCompany.find(c => c.Nempresa === vesselInfo?.Nempresa)?.Tempresa;
        return companyInfo || "Sin empresa";
    }

    return (
        <div className='containerGridStatus'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('gridStatus.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('gridStatus.liveGrid')}</h4>
                        <h6>{dateTemp}</h6>
                    </div>

                    <div className='filter'>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="all">{t('gridStatus.all')}</option>
                            {statusOptions.map((status, index) => (
                                <option key={index} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>

                    <p className="alert-warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        &nbsp; {t('gridStatus.alertMessage')}
                    </p>

                    <div className='conteo'>
                        <h6>{t('gridStatus.thisPageWillReloadIn')}: {counter} {t('gridStatus.seconds')}</h6>
                    </div>

                    {filteredGridPoints.length > 0 && filteredGridPoints[0]?.name ? (
                        <div className='separator'>
                            {filteredGridPoints.map((gridPoint) => (
                                <GridPoint
                                    key={gridPoint.name}
                                    id={gridPoint.id}
                                    idmonaguillo={gridPoint.idmonaguillo}
                                    name={gridPoint.name}
                                    active={gridPoint.active}
                                    orden={gridPoint.orden}
                                    disponible={gridPoint.disponible}
                                    initialStatus={gridPoint.initialStatus}
                                    initialStatus2={gridPoint.initialStatus2}
                                    onStatusChange={handleStatusChange}
                                    reserva={gridPoint.reserva || null}
                                    vessel={dataVessel}
                                    onShowModalChange={setShowModal}
                                    company={selectCompany(gridPoint.reserva)}
                                />
                            ))}
                        </div>
                    ) : (
                        <p style={{ textAlign: "center", color: "gray", width: "100%" }}>{t('gridStatus.thereAreNoNetworkPointsAvailable')}</p>
                    )}
                </>
            )}
        </div>
    );
};

export default GridStatus;